import { Box, Dialog, Grid, IconButton, Typography, Breadcrumbs, Link as MuiLink } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReportsNavigation } from './ReportsNavigation';
import { ReportsViewer } from './ReportsViewer';
import { useEffect, useState, useMemo } from 'react';

export const ReportsDialog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ '*': string }>();
  
  // Extract file path from URL: /reports/view/path/to/file.md
  const filePath = params['*'] 
    ? decodeURIComponent(params['*'])
    : undefined;

  const [selectedPath, setSelectedPath] = useState<string | undefined>(filePath);

  // Update selectedPath when URL changes
  useEffect(() => {
    if (filePath) {
      setSelectedPath(filePath);
    }
  }, [filePath]);

  const handleClose = () => {
    navigate('/reports');
  };

  const handleFileSelect = (newFilePath: string) => {
    // Only update if it's a different file to avoid unnecessary re-renders
    if (newFilePath !== selectedPath) {
      // Update URL when file is selected
      navigate(`/reports/view/${encodeURIComponent(newFilePath)}`, { replace: true });
      setSelectedPath(newFilePath);
    }
  };

  // Determine which report folder we're viewing (for navigation tree)
  // If selectedPath is a file, use its parent folder for navigation
  // If selectedPath is a folder, use it directly for navigation
  const isViewingFile = selectedPath?.includes('.') && selectedPath.lastIndexOf('.') > selectedPath.lastIndexOf('/');
  
  // Get the report folder path (first part of the path) - this is the root investigation folder
  const reportFolderPath = selectedPath?.includes('/')
    ? selectedPath.split('/')[0]
    : selectedPath || '';

  // Navigation prefix: when viewing a file, show its parent folder in the tree
  // When viewing a folder, show that folder in the tree
  // Keep the navigation prefix stable when selecting different files in the same folder
  const [navigationPrefix, setNavigationPrefix] = useState<string>('');
  
  useEffect(() => {
    if (selectedPath) {
      if (isViewingFile && selectedPath.includes('/')) {
        // File: use parent folder
        const parentFolder = selectedPath.substring(0, selectedPath.lastIndexOf('/'));
        // Only update navigation prefix if the folder context actually changed
        // This keeps the tree stable when selecting different files in the same folder
        setNavigationPrefix(prev => {
          // If we're already showing this folder, don't change it
          if (prev === parentFolder) {
            return prev;
          }
          // If we're showing a different folder or nothing, update to the new folder
          return parentFolder;
        });
      } else {
        // Folder: use the folder itself
        // Always update when navigating to a different folder
        setNavigationPrefix(selectedPath);
      }
    } else {
      setNavigationPrefix('');
    }
  }, [selectedPath, isViewingFile]);

  // Check if we can navigate up (i.e., we're in a subfolder, not at root investigation level)
  const canNavigateUp = navigationPrefix && navigationPrefix !== reportFolderPath && navigationPrefix.includes('/');

  // Parse the investigation title from the folder path
  // Format: YYYY-MM-DD-{number}-{PascalCaseName}
  const investigationTitle = useMemo(() => {
    const folderName = reportFolderPath;
    const match = folderName.match(/^(\d{4}-\d{2}-\d{2})-(\d+)-(.+)$/);
    if (!match) {
      return folderName; // Fallback to folder name if it doesn't match pattern
    }

    const [, date, , pascalCaseName] = match;

    // Convert PascalCase to human-readable: "ExpiredAuth0Tokens" -> "Expired Auth0 Tokens"
    const humanReadable = pascalCaseName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^ /, '') // Remove leading space
      .trim();

    return `${date} - ${humanReadable}`;
  }, [reportFolderPath]);

  // Generate breadcrumb segments from the current path
  const breadcrumbSegments = useMemo(() => {
    if (!selectedPath) {
      return [];
    }

    const segments: Array<{ label: string; path: string }> = [];
    
    // Add root segment
    segments.push({ label: 'Root', path: '' });
    
    // Split path and create segments
    const parts = selectedPath.split('/').filter(Boolean);
    let currentPath = '';
    
    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      segments.push({
        label: part,
        path: currentPath,
      });
    });
    
    return segments;
  }, [selectedPath]);

  const handleBreadcrumbClick = (path: string) => {
    if (path === '') {
      // Navigate to root (reports list)
      navigate('/reports');
    } else {
      // Navigate to the selected path
      navigate(`/reports/view/${encodeURIComponent(path)}`, { replace: true });
      setSelectedPath(path);
    }
  };

  const handleNavigateUp = () => {
    if (canNavigateUp && navigationPrefix) {
      // Navigate to the parent of the current navigation prefix
      const parentPath = navigationPrefix.substring(0, navigationPrefix.lastIndexOf('/'));
      navigate(`/reports/view/${encodeURIComponent(parentPath)}`, { replace: true });
      setSelectedPath(parentPath);
    }
  };

  const handleNavigateRoot = () => {
    navigate('/reports');
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'background.default',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <IconButton onClick={handleClose} size="small" sx={{ mr: 1 }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
              {investigationTitle}
            </Typography>
          </Box>
          {breadcrumbSegments.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ ml: 5 }}
            >
              {breadcrumbSegments.map((segment, index) => {
                const isLast = index === breadcrumbSegments.length - 1;
                return (
                  <MuiLink
                    key={segment.path || 'root'}
                    component="button"
                    variant="body2"
                    onClick={() => !isLast && handleBreadcrumbClick(segment.path)}
                    sx={{
                      color: isLast ? 'text.primary' : 'primary.main',
                      textDecoration: isLast ? 'none' : 'underline',
                      cursor: isLast ? 'default' : 'pointer',
                      '&:hover': {
                        textDecoration: isLast ? 'none' : 'underline',
                      },
                    }}
                  >
                    {segment.label}
                  </MuiLink>
                );
              })}
            </Breadcrumbs>
          )}
        </Box>

        {/* Content: File tree and viewer */}
        <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Grid
            item
            xs={3}
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              overflow: 'auto',
              height: '100%',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Files
              </Typography>
              <ReportsNavigation
                prefix={navigationPrefix}
                onFileSelect={handleFileSelect}
                selectedPath={selectedPath}
                onNavigateUp={canNavigateUp ? handleNavigateUp : undefined}
                onNavigateRoot={handleNavigateRoot}
              />
            </Box>
          </Grid>
          <Grid item xs={9} sx={{ overflow: 'auto', height: '100%' }}>
            {selectedPath && isViewingFile ? (
              <ReportsViewer
                filePath={selectedPath}
                currentPath={selectedPath}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: 'text.secondary',
                  p: 2,
                }}
              >
                <Typography variant="body2">
                  {selectedPath
                    ? 'Select a file from the navigation to view it'
                    : 'Select a report folder or file from the navigation'}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

